import React, { useState } from 'react';
import { Box } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import FieldDetails from './FieldDetails';
import { getFormattedDate } from '../../lib/dates';
import {
  downloadNProtectantPdfs,
  downloadNProtectantShapefile,
} from '../../lib/api';
import { generateCsvDownload } from '../../lib/csv';
import { getRounded } from '../../lib/numbers';
import { usePermissions } from '../../hooks/use-permissions';
import { DownloadButton } from '../../components/DownloadButton';
import { Table } from '../../components/table/Table';
import { TableStatus } from '../../components/table/TableStatus';
import DownloadDialog, { timings } from './DownloadDialog';
import { DownloadOptions } from './DownloadOptions';
import { useGrowers } from '../../hooks/use-growers';
import { SendToDeereButton } from '../../components/deere/SendToDeereButton';

export default function FieldNProtectant(props) {
  const { fields, loading, selectedFields, reloadFields } = props;
  const { selectedId: growerId } = useGrowers();
  const [showStats, setShowStats] = useState(false);
  const [statsField, setStatsField] = useState(null);
  const [selectedTiming, setSelectedTiming] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [downloadType, setDownloadType] = useState(null);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const permissions = usePermissions();

  return (
    <div style={{ height: '100%' }}>
      <Table
        actions={buildActions()}
        loading={loading}
        fields={fields || []}
        columns={buildColumns()}
        isLpkContractRestricted={true}
      />
      {showStats && statsField && (
        <FieldDetails
          field={statsField}
          handleClose={hideStats}
          reloadFields={reloadFields}
        />
      )}
      {buildDownloadShapefileModal()}
      {buildPrintPdfModal()}
    </div>
  );

  function buildTableStatus(row, key) {
    const status = row[`${key}Status`];
    const avg = row[`${key}Avg`];
    return <TableStatus status={/complete/i.test(status) ? avg : status} />;
  }

  function buildColumns() {
    return [
      {
        field: 'plannedCrop',
        headerName: 'Planned Crop',
        flex: 0.5,
        minWidth: 75,
        align: 'center',
      },
      {
        field: 'fallEarlySpringAvg',
        headerName: 'Fall/Early Spring Avg',
        flex: 1,
        minWidth: 150,
        headerAlign: 'right',
        align: 'right',
        type: 'string',
        renderCell: ({ row }) => (
          <Box sx={{ display: 'flex', justifyContent: 'right' }}>
            {buildTableStatus(row, 'fallEarlySpring')}
          </Box>
        ),
      },
      {
        field: 'springAvg',
        headerName: 'Spring Avg',
        flex: 1,
        minWidth: 150,
        headerAlign: 'right',
        align: 'right',
        type: 'string',
        renderCell: ({ row }) => (
          <Box sx={{ display: 'flex', justifyContent: 'right' }}>
            {buildTableStatus(row, 'spring')}
          </Box>
        ),
      },
      {
        field: 'earlySidesdressAvg',
        headerName: 'Early Sidedress Avg',
        flex: 1,
        type: 'string',
        headerAlign: 'right',
        align: 'right',
        minWidth: 200,
        renderCell: ({ row }) => (
          <Box sx={{ display: 'flex', justifyContent: 'right' }}>
            {buildTableStatus(row, 'earlySidedress')}
          </Box>
        ),
      },
      {
        field: 'lateSidesdressAvg',
        headerName: 'Late Sidedress Avg',
        flex: 1,
        type: 'string',
        headerAlign: 'right',
        align: 'right',
        minWidth: 150,
        renderCell: ({ row }) => (
          <Box sx={{ display: 'flex', justifyContent: 'right' }}>
            {buildTableStatus(row, 'lateSidedress')}
          </Box>
        ),
      },
      {
        field: 'created',
        headerName: 'Created',
        flex: 0.5,
        type: 'date',
        minWidth: 90,
        valueFormatter: ({ value }) => getFormattedDate(value),
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Adjust',
        getActions: ({ row }) => [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Adjust"
            onClick={(e) => onAdjustClick(e, row)}
            disabled={!row.stats}
            data-testid={`adjust-${row.aaManagedId}`}
          />,
        ],
      },
    ];
  }

  function buildDownloadShapefileModal() {
    return (
      downloadType === 'shapefile' &&
      downloadOpen && (
        <DownloadDialog
          title="Export Rx"
          fields={getDownloadablePrescriptions()}
          onClose={handleDownloadSelection}
        />
      )
    );
  }

  function buildPrintPdfModal() {
    return (
      downloadType === 'pdf' &&
      downloadOpen && (
        <DownloadDialog
          title="Print to PDF"
          fields={getDownloadablePrescriptions()}
          onClose={handleDownloadSelection}
        />
      )
    );
  }

  function buildActions() {
    return [
      permissions.includes('DOWNLOAD_RX') && (
        <DownloadButton
          key="download"
          data-testid="download-shapefiles"
          disabled={!getDownloadableAaids().length}
          preDownload={handleDownload}
          autoDownload={(() =>
            selectedTiming && selectedProduct && downloadType === 'shapefile'
              ? downloadShapefiles
              : undefined)()}
          postDownload={() => {
            setSelectedProduct(null);
            setSelectedTiming(null);
          }}
          keep-open="true"
        >
          Download
        </DownloadButton>
      ),
      <DownloadButton
        disabled={!fields.length}
        key="csv"
        download={downloadCsv}
      >
        Export CSV
      </DownloadButton>,
      <DownloadButton
        key="pdf"
        disabled={!getDownloadableAaids().length}
        preDownload={handlePrintPdf}
        autoDownload={
          selectedTiming && selectedProduct && downloadType === 'pdf'
            ? downloadPdf
            : undefined
        }
        postDownload={() => {
          setSelectedProduct(null);
          setSelectedTiming(null);
          setDownloadType(null);
        }}
        keep-open="true"
      >
        Print to PDF
      </DownloadButton>,
      <SendToDeereButton
        type="nprotectant"
        prescriptions={getDownloadablePrescriptions()}
        Options={DownloadOptions}
        prescriptionMapper={(options, rx) => ({ ...rx, ...options })}
      />,
    ].filter(Boolean);
  }

  function hideStats() {
    setShowStats(false);
    setStatsField(null);
  }

  function handleDownload() {
    setDownloadOpen(true);
    setDownloadType('shapefile');
  }

  function handleDownloadSelection(timing, product) {
    setDownloadOpen(false);
    setSelectedTiming(timing);
    setSelectedProduct(product);
  }

  function downloadShapefiles() {
    return downloadNProtectantShapefile(
      growerId,
      getDownloadableAaids(),
      selectedTiming,
      selectedProduct,
    );
  }

  function downloadCsv() {
    const filename = `n-protectant-export-${growerId}.csv`;
    const csvArr =
      selectedFields && selectedFields.length > 0
        ? fields.filter((f) =>
            selectedFields.map((s) => s.aaManagedId).includes(f.aaManagedId),
          )
        : fields;
    const csvData = csvArr.map((f) => ({
      grower: f.growerOperationName,
      farm_name: f.farmName,
      field_name: f.fieldName,
      field_id: f.fieldId,
      acres: getRounded(f.acres, 2),
      aaid: f.aaManagedId,
      fall_early_spring_product: getProduct(f.fallEarlySpringTotal),
      fall_early_spring_created: getFormattedDate(f.fallEarlySpringCreated),
      fall_early_spring_status: f.fallEarlySpringStatus,
      fall_early_spring_avg: toNumber(f.fallEarlySpringAvg),
      fall_early_spring_min: toNumber(f.fallEarlySpringMin),
      fall_early_spring_max: toNumber(f.fallEarlySpringMax),
      fall_early_spring_total: toNumber(f.fallEarlySpringTotal),
      spring_product: getProduct(f.springTotal),
      spring_created: getFormattedDate(f.springCreated),
      spring_status: f.springStatus,
      spring_avg: toNumber(f.springAvg),
      spring_min: toNumber(f.springMin),
      spring_max: toNumber(f.springMax),
      spring_total: toNumber(f.springTotal),
      early_sidedress_product: getProduct(f.earlySidedressTotal),
      early_sidedress_created: getFormattedDate(f.earlySidedressCreated),
      early_sidedress_status: f.earlySidedressStatus,
      early_sidedress_avg: toNumber(f.earlySidedressAvg),
      early_sidedress_min: toNumber(f.earlySidedressMin),
      early_sidedress_max: toNumber(f.earlySidedressMax),
      early_sidedress_total: toNumber(f.earlySidedressTotal),
      late_sidedress_product: getProduct(f.lateSidedressTotal),
      late_sidedress_created: getFormattedDate(f.lateSidedressCreated),
      late_sidedress_status: f.lateSidedressStatus,
      late_sidedress_avg: toNumber(f.lateSidedressAvg),
      late_sidedress_min: toNumber(f.lateSidedressMin),
      late_sidedress_max: toNumber(f.lateSidedressMax),
      late_sidedress_total: toNumber(f.lateSidedressTotal),
    }));

    generateCsvDownload(csvData, filename);
  }

  function toNumber(value) {
    const number = parseFloat(value);
    return Number.isNaN(number) ? null : number;
  }

  function getProduct(product) {
    const matcher = /.* oz (.*)/;
    const match = matcher.exec(product);
    return match ? match[1] : product;
  }

  function handlePrintPdf() {
    setDownloadType('pdf');
    setDownloadOpen(true);
  }

  function downloadPdf() {
    return downloadNProtectantPdfs(
      growerId,
      getDownloadableAaids(),
      selectedTiming,
      selectedProduct,
    );
  }

  function onAdjustClick(event, field) {
    event.stopPropagation();
    event.preventDefault();
    setStatsField(field);
    setShowStats(true);
  }

  function isFieldDownloadable(field) {
    const foundField = fields.find((f) => f.aaManagedId === field.aaManagedId);
    const timing = timings.find((t) => (t.variant || t.key) === selectedTiming);
    if (!foundField?.stats) return false;
    if (!timing && !selectedProduct) return true;
    return foundField?.stats[timing.key]?.product === selectedProduct;
  }

  function getDownloadableAaids() {
    return selectedFields.filter(isFieldDownloadable).map((f) => f.aaManagedId);
  }

  function getDownloadablePrescriptions() {
    const aaids = getDownloadableAaids();
    return fields?.filter((f) => aaids.includes(f.aaManagedId));
  }
}
